import { layoutFromMap } from 'entitree-flex';
import { ConnectionLineType, Edge, MarkerType, Node, Position } from 'reactflow';
import { Outcome } from 'types';
import { DEFAULT_WORKSPACE_HEIGHT, MapNode } from '..';

const nodeWidth = 470;
const defaultHeight = 240;

const Orientation = {
  Vertical: 'vertical',
  Horizontal: 'horizontal',
};

const entitreeSettings = {
  clone: true, // returns a copy of the input, if your application does not allow editing the original object
  enableFlex: true, // has slightly better performance if turned off (node.width, node.height will not be read)
  firstDegreeSpacing: 50, // spacing in px between nodes belonging to the same source, e.g. children with same parent
  nextAfterAccessor: 'spouses', // the side node prop used to go sideways, AFTER the current node
  nextAfterSpacing: 50, // the spacing of the "side" nodes AFTER the current node
  nextBeforeAccessor: 'siblings', // the side node prop used to go sideways, BEFORE the current node
  nextBeforeSpacing: 50, // the spacing of the "side" nodes BEFORE the current node
  nodeHeight: defaultHeight, // default node height in px
  nodeWidth, // default node width in px
  orientation: Orientation.Vertical, // "vertical" to see parents top and children bottom, "horizontal" to see parents left and
  rootX: 0, // set root position if other than 0
  rootY: 0, // set root position if other than 0
  secondDegreeSpacing: 50, // spacing in px between nodes not belonging to same parent eg "cousin" nodes
  sourcesAccessor: 'parents', // the prop used as the array of ancestors ids
  sourceTargetSpacing: 100, // the "vertical" spacing between nodes in vertical orientation, horizontal otherwise
  targetsAccessor: 'children', // the prop used as the array of children ids
};

const { Top, Bottom } = Position;

const updateNodeHeights = (tree: Record<string, MapNode>) => {
  Object.keys(tree).forEach((key) => {
    const node = tree[key];
    let height = defaultHeight;
    let width = nodeWidth;
    if (node.meta.loadedHeight && node.meta.contentExpanded) {
      height = node.meta.loadedHeight;
    } else if (node.meta.outcomeHeight) {
      height = node.meta.outcomeHeight;
    } else if (node.type === 'smallPlan') {
      height = DEFAULT_WORKSPACE_HEIGHT;
    }
    tree[key] = { ...node, height, width };
  });
  return tree;
};

export const outcomeLayoutElements = (
  tree: Record<string, MapNode>,
  rootId: string,
  addDependentOutcomes: (nodeId: string, outcomes: Outcome[]) => void,
  toggleExpand: (nodeId: string, expanded: boolean) => void,
  saveContentExpanded?: (nodeId: string, height: number, contentExpanded: boolean) => void,
  saveOutcomeNodeHeight?: (nodeId: string, height: number) => void,
) => {
  const modifiedTree = updateNodeHeights(tree);
  const { nodes: entitreeNodes, rels: entitreeEdges } = layoutFromMap(rootId, modifiedTree, {
    ...entitreeSettings,
    orientation: 'vertical',
  });

  const nodes: Node[] = [],
    edges: Edge[] = [];

  entitreeEdges.forEach((edge) => {
    edges.push({
      id: `e-${edge.source.id}-${edge.target.id}`,
      source: edge.source.id, // ← This should be the parent
      target: edge.target.id, // ← This should be the child
      sourceHandle: Bottom,
      targetHandle: Top,
      type: ConnectionLineType.SmoothStep,
      markerStart: {
        type: MarkerType.ArrowClosed,
        width: 25,
        height: 25,
      },
    });
  });

  entitreeNodes.forEach((node) => {
    const newNode: any = {
      id: node.id,
      type: node.type ?? 'default',
      position: { x: node.x, y: node.y },
      hidden: node.type === 'default',
      data: {
        label: node.name,
        toggleExpand,
        saveContentExpanded,
        saveOutcomeNodeHeight,
        addDependentOutcomes,
        ...node,
      },
      width: node.width,
      height: node.height,
      sourcePosition: Bottom,
      targetPosition: Top,
    };
    nodes.push(newNode);
  });

  return { nodes, edges };
};
